/**
 * Finishd Design System Primitives
 * Exact recreation from extracted_assets_2
 * All styles match inline styles from the original design files
 */

import type { LucideIcon } from "lucide-react";
import {
	ArrowLeft,
	ArrowRight,
	Bell,
	Calendar,
	Check,
	CheckCircle,
	CheckSquare,
	Compass,
	Copy,
	Download,
	Eye,
	FileText,
	Flag,
	Folder,
	Grid,
	Heart,
	Home,
	Image,
	Inbox,
	Info,
	Link,
	Mail,
	MapPin,
	MessageSquare,
	Package,
	Paperclip,
	Pen,
	Phone,
	Plus,
	Printer,
	RefreshCw,
	Search,
	Send,
	Sparkles,
	Star,
	Trash,
	Upload,
	Users,
	Wallet,
	X,
} from "lucide-react";
import * as React from "react";
import type { AvatarTone } from "../../data/mock-homeowner";
import type { IconName } from "../../types/icon-name";

export type { AvatarTone } from "../../data/mock-homeowner";

type PillTone = "active" | "awaiting" | "done" | "risk" | "neutral" | "info";

type BtnVariant = "primary" | "ghost" | "quiet" | "dark" | "onDark";

type BtnSize = "xs" | "sm" | "md" | "lg";

// ============================================================================
// HO_STYLES (from c34a46ad)
// ============================================================================

export const hoStyles = {
	page: { paddingTop: 0 },
	section: { maxWidth: 1240, margin: "0 auto", padding: "0 48px 8px" as const },
	head: {
		maxWidth: 1240,
		margin: "0 auto",
		padding: "56px 48px 24px" as const,
		display: "flex" as const,
		justifyContent: "space-between" as const,
		alignItems: "flex-end" as const,
		gap: 24,
		flexWrap: "wrap" as const,
	},
	title: {
		fontFamily: "var(--font-display)",
		fontWeight: 500,
		fontSize: 48,
		lineHeight: 1.0,
		letterSpacing: "-0.025em",
		color: "var(--color-dashboard-text)",
		margin: "12px 0 0",
	},
	h2: {
		fontFamily: "var(--font-display)",
		fontWeight: 500,
		fontSize: 34,
		lineHeight: 1.05,
		letterSpacing: "-0.02em",
		color: "var(--color-dashboard-text)",
		margin: 0,
	},
};

// ============================================================================
// ICON SYSTEM — Lucide-backed
// ============================================================================

export const ICON_MAP = {
	arrowLeft: ArrowLeft,
	arrowRight: ArrowRight,
	bell: Bell,
	calendar: Calendar,
	check: Check,
	checkCircle: CheckCircle,
	checkSquare: CheckSquare,
	compass: Compass,
	copy: Copy,
	download: Download,
	eye: Eye,
	fileText: FileText,
	flag: Flag,
	folder: Folder,
	grid: Grid,
	heart: Heart,
	home: Home,
	image: Image,
	inbox: Inbox,
	info: Info,
	link: Link,
	mail: Mail,
	mapPin: MapPin,
	message: MessageSquare,
	package: Package,
	paperclip: Paperclip,
	pen: Pen,
	phone: Phone,
	plus: Plus,
	printer: Printer,
	refreshCw: RefreshCw,
	search: Search,
	send: Send,
	sparkles: Sparkles,
	star: Star,
	trash: Trash,
	upload: Upload,
	users: Users,
	wallet: Wallet,
	x: X,
} as const satisfies Record<IconName, LucideIcon>;

export function Icn({
	name,
	size = 16,
	color,
	style,
	fill,
}: {
	name: IconName;
	size?: number;
	color?: string;
	style?: React.CSSProperties;
	fill?: string;
}) {
	const Icon = ICON_MAP[name];
	if (!Icon) return null;
	return (
		// The stroke is currentColor and the color goes through CSS, so a
		// var(--color-dashboard-*) token resolves in every browser.
		<Icon
			size={size}
			color="currentColor"
			fill={fill ?? "transparent"}
			strokeWidth={1.6}
			style={{
				flexShrink: 0,
				color: color ?? "var(--color-dashboard-text)",
				...style,
			}}
		/>
	);
}

// ============================================================================
// AVATAR PALETTES
// ============================================================================

const AV_PALETTES: Record<AvatarTone, { bg: string; fg: string }> = {
	amber: {
		bg: "linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-warm-200))",
		fg: "var(--color-dashboard-orange-text-on-tint)",
	},
	ink: {
		bg: "var(--color-dashboard-dark)",
		fg: "var(--color-dashboard-on-photo-warm)",
	},
	stone: {
		bg: "var(--color-dashboard-divider)",
		fg: "var(--color-dashboard-muted-dark)",
	},
	cognac: {
		bg: "linear-gradient(135deg, var(--color-dashboard-muted-dark), #334155)",
		fg: "var(--color-dashboard-on-fill)",
	},
	terra: {
		bg: "linear-gradient(135deg, var(--color-dashboard-accent-light), var(--color-dashboard-accent-dark))",
		fg: "var(--color-dashboard-on-fill)",
	},
};

export function Avatar({
	initials,
	tone = "amber",
	size = 36,
	ring,
	style,
}: {
	initials: string;
	tone?: AvatarTone;
	size?: number;
	ring?: string;
	style?: React.CSSProperties;
}) {
	const p = AV_PALETTES[tone] || AV_PALETTES.amber;
	return (
		<div
			style={{
				width: size,
				height: size,
				borderRadius: "50%",
				background: p.bg,
				color: p.fg,
				flexShrink: 0,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				fontFamily: "var(--font-ui)",
				fontWeight: 700,
				fontSize: Math.round(size * 0.32),
				boxShadow:
					(ring
						? `0 0 0 2px var(--color-dashboard-paper), 0 0 0 3.5px ${ring}, `
						: "") +
					"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 45%, transparent), 0 2px 4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)",
				...style,
			}}
		>
			{initials}
		</div>
	);
}

// ============================================================================
// PILL TONES
// ============================================================================

export const PILL_TONES: Record<
	PillTone,
	{ bg: string; fg: string; dot: string }
> = {
	active: {
		bg: "color-mix(in srgb, var(--color-dashboard-accent) 10%, transparent)",
		fg: "var(--color-dashboard-accent-dark)",
		dot: "var(--color-dashboard-accent)",
	},
	awaiting: {
		bg: "var(--color-dashboard-pill-awaiting-bg)",
		fg: "var(--color-dashboard-pill-awaiting)",
		dot: "var(--color-dashboard-pill-awaiting)",
	},
	done: {
		bg: "var(--color-dashboard-pill-done-bg)",
		fg: "var(--color-dashboard-success)",
		dot: "var(--color-dashboard-success-fill)",
	},
	risk: {
		bg: "var(--color-dashboard-pill-risk-bg)",
		fg: "var(--color-dashboard-danger)",
		dot: "var(--color-dashboard-danger-fill)",
	},
	neutral: {
		bg: "var(--color-dashboard-divider)",
		fg: "var(--color-dashboard-muted-dark)",
		dot: "var(--color-dashboard-muted)",
	},
	info: {
		bg: "var(--color-dashboard-pill-info-bg)",
		fg: "var(--color-dashboard-info-strong)",
		dot: "var(--color-dashboard-pill-info)",
	},
};

export function Pill({
	tone = "neutral",
	children,
	dot = true,
	style,
}: {
	tone?: PillTone;
	children: React.ReactNode;
	dot?: boolean;
	style?: React.CSSProperties;
}) {
	const t = PILL_TONES[tone] || PILL_TONES.neutral;
	return (
		<span
			style={{
				display: "inline-flex",
				alignItems: "center",
				gap: 6,
				height: 24,
				padding: "0 11px",
				borderRadius: 999,
				background: t.bg,
				color: t.fg,
				fontFamily: "var(--font-ui)",
				fontSize: 11,
				fontWeight: 600,
				letterSpacing: "0.01em",
				whiteSpace: "nowrap",
				...style,
			}}
		>
			{dot && (
				<span
					style={{
						width: 6,
						height: 6,
						borderRadius: "50%",
						background: t.dot,
						flexShrink: 0,
					}}
				/>
			)}
			{children}
		</span>
	);
}

// ============================================================================
// PROGRESS BAR
// ============================================================================

export function Progress({
	value = 0,
	height = 4,
	track = "var(--color-dashboard-border)",
	fill = "var(--color-dashboard-accent)",
	style,
}: {
	value?: number;
	height?: number;
	track?: string;
	fill?: string;
	style?: React.CSSProperties;
}) {
	return (
		<div
			style={{
				height,
				borderRadius: 69,
				background: track,
				overflow: "hidden",
				...style,
			}}
		>
			<div
				style={{
					width: `${Math.max(0, Math.min(100, value))}%`,
					height: "100%",
					background: fill,
					borderRadius: 69,
					transition: "width 600ms cubic-bezier(0.22,1,0.36,1)",
				}}
			/>
		</div>
	);
}

// ============================================================================
// FLOAT PANEL
// ============================================================================

const FLOAT_SHADOWS: Record<number, string> = {
	1: "var(--dashboard-shadow-1)",
	2: "var(--dashboard-shadow-2)",
	3: "var(--dashboard-shadow-3)",
	4: "var(--dashboard-shadow-4)",
};

export function FloatPanel({
	children,
	elev = 2,
	padding = 24,
	style,
	onClick,
	onMouseEnter,
	onMouseLeave,
}: {
	children: React.ReactNode;
	elev?: number;
	padding?: number;
	style?: React.CSSProperties;
	onClick?: () => void;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
}) {
	return (
		// biome-ignore lint/a11y/noStaticElementInteractions: design system primitive that conditionally acts as button
		<div
			role={onClick ? "button" : undefined}
			tabIndex={onClick ? 0 : undefined}
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onKeyDown={
				onClick
					? (e) => {
							if (e.target !== e.currentTarget) return;
							if (e.key === "Enter" || e.key === " ") onClick();
						}
					: undefined
			}
			style={{
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
				borderRadius: 13,
				padding,
				boxShadow: FLOAT_SHADOWS[elev],
				...style,
			}}
		>
			{children}
		</div>
	);
}

// ============================================================================
// CHIP
// ============================================================================

export function Chip({
	active,
	onClick,
	children,
	style,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
	style?: React.CSSProperties;
}) {
	const [hover, setHover] = React.useState(false);
	return (
		<button
			type="button"
			onClick={onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				height: 32,
				padding: "0 14px",
				border: 0,
				borderRadius: 999,
				fontFamily: "var(--font-body)",
				fontSize: 12,
				fontWeight: active ? 600 : 500,
				color: active
					? "var(--color-dashboard-on-fill)"
					: hover
						? "var(--color-dashboard-text)"
						: "var(--color-dashboard-muted-dark)",
				background: active
					? "var(--color-dashboard-accent)"
					: hover
						? "var(--color-dashboard-divider)"
						: "transparent",
				boxShadow: active
					? "inset 0 1px 0 rgba(255,237,213,0.35), 0 6px 18px -6px color-mix(in srgb, var(--color-dashboard-accent) 35%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)"
					: hover
						? "inset 0 0 0 1px var(--color-border-default), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent)"
						: "inset 0 0 0 1px var(--color-border-default)",
				cursor: "pointer",
				transition: "all 200ms cubic-bezier(0.22, 1, 0.36, 1)",
				letterSpacing: "0.005em",
				...style,
			}}
		>
			{children}
		</button>
	);
}

// ============================================================================
// BUTTON
// ============================================================================

export function Btn({
	variant = "primary",
	size = "md",
	icon,
	iconRight,
	children,
	style,
	...rest
}: {
	variant?: BtnVariant;
	size?: BtnSize;
	icon?: React.ReactNode;
	iconRight?: React.ReactNode;
	children?: React.ReactNode;
	style?: React.CSSProperties;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	disabled?: boolean;
}) {
	const [hoverState, setHover] = React.useState(false);
	const [pressState, setPress] = React.useState(false);
	// A disabled button never shows hover or press, in any variant.
	const hover = hoverState && !rest.disabled;
	const press = pressState && !rest.disabled;

	const heights = { xs: 30, sm: 36, md: 44, lg: 52 };
	const fontSizes = { xs: 11.5, sm: 12.5, md: 13.5, lg: 14.5 };
	const radii = { xs: 6, sm: 6, md: 8, lg: 9 };
	const paddings = {
		xs: "0 12px",
		sm: "0 16px",
		md: "0 22px",
		lg: "0 30px",
	};

	const base: React.CSSProperties = {
		height: heights[size],
		padding: paddings[size],
		fontSize: fontSizes[size],
		borderRadius: radii[size],
		fontFamily: "var(--font-body)",
		fontWeight: 600,
		letterSpacing: "0.005em",
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		gap: 8,
		border: 0,
		cursor: rest.disabled ? "not-allowed" : "pointer",
		whiteSpace: "nowrap",
		position: "relative",
		opacity: rest.disabled ? 0.6 : 1,
		transition:
			"transform 250ms cubic-bezier(0.22, 1, 0.36, 1), box-shadow 250ms cubic-bezier(0.22, 1, 0.36, 1), background 200ms ease, color 200ms ease",
		transform:
			press || rest.disabled
				? "translateY(0)"
				: hover
					? "translateY(-1px)"
					: "translateY(0)",
	};

	const variants: Record<BtnVariant, React.CSSProperties> = {
		primary: {
			color: "var(--color-dashboard-on-fill)",
			background: press
				? "linear-gradient(180deg, rgba(0,0,0,0.08), rgba(0,0,0,0.04)), linear-gradient(180deg, #9A3412, #7C2D12)"
				: "var(--color-dashboard-accent)",
			boxShadow: press
				? "inset 0 2px 4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 25%, transparent), inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 10%, transparent)"
				: hover
					? "inset 0 1px 0 rgba(255,237,213,0.45), inset 0 -1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 2px 4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent), 0 10px 24px -6px color-mix(in srgb, var(--color-dashboard-accent) 45%, transparent), 0 18px 44px -12px color-mix(in srgb, var(--color-dashboard-accent) 40%, transparent)"
					: "inset 0 1px 0 rgba(255,237,213,0.35), inset 0 -1px 0 color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent), 0 6px 18px -6px color-mix(in srgb, var(--color-dashboard-accent) 35%, transparent), 0 12px 32px -10px color-mix(in srgb, var(--color-dashboard-accent) 30%, transparent)",
		},
		ghost: {
			color: hover
				? "var(--color-dashboard-text)"
				: "var(--color-dashboard-muted-dark)",
			background: press
				? "var(--color-dashboard-border)"
				: hover
					? "var(--color-dashboard-divider)"
					: "transparent",
			boxShadow: press
				? "inset 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 8%, transparent)"
				: "none",
		},
		quiet: {
			color: "var(--color-dashboard-text)",
			background: hover
				? "color-mix(in srgb, var(--color-dashboard-accent) 4%, transparent)"
				: "transparent",
			boxShadow: hover
				? "inset 0 0 0 1.5px var(--color-dashboard-accent), 0 4px 12px -4px color-mix(in srgb, var(--color-dashboard-accent) 15%, transparent)"
				: "inset 0 0 0 1.5px var(--color-dashboard-muted-light)",
		},
		dark: {
			color: "var(--color-dashboard-on-fill)",
			background: press
				? "#020617"
				: hover
					? "var(--color-dashboard-dark-elevated)"
					: "var(--color-dashboard-dark)",
			boxShadow: hover
				? "0 8px 24px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 50%, transparent)"
				: "0 2px 6px color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent)",
		},
		onDark: {
			color: "var(--color-dashboard-on-fill)",
			// White glass on a dark surface, the same in both modes.
			background: press
				? "color-mix(in srgb, var(--color-dashboard-on-fill) 5%, transparent)"
				: hover
					? "color-mix(in srgb, var(--color-dashboard-on-fill) 14%, transparent)"
					: "color-mix(in srgb, var(--color-dashboard-on-fill) 8%, transparent)",
			boxShadow:
				"inset 0 0 0 1px color-mix(in srgb, var(--color-dashboard-on-fill) 18%, transparent)",
		},
	};

	return (
		<button
			{...rest}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => {
				setHover(false);
				setPress(false);
			}}
			onMouseDown={() => setPress(true)}
			onMouseUp={() => setPress(false)}
			style={{ ...base, ...variants[variant], ...style }}
		>
			{icon}
			{children}
			{iconRight}
		</button>
	);
}

// ============================================================================
// SECONDARY BUTTON SIZING
// ============================================================================

/**
 * Metrics the homeowner screens use on a secondary button, as inline style
 * for `StudioButton`. StudioButton draws the fill, ring, hover and disabled
 * look; this keeps the type size, radius, padding and weight those screens
 * already had.
 */
export function secondaryBtnStyle(size: BtnSize): React.CSSProperties {
	const fontSizes = { xs: 11.5, sm: 12.5, md: 13.5, lg: 14.5 };
	const radii = { xs: 6, sm: 6, md: 8, lg: 9 };
	const paddingsX = { xs: 12, sm: 16, md: 22, lg: 30 };
	return {
		fontSize: fontSizes[size],
		borderRadius: radii[size],
		paddingLeft: paddingsX[size],
		paddingRight: paddingsX[size],
		fontWeight: 600,
		letterSpacing: "0.005em",
		whiteSpace: "nowrap",
		position: "relative",
	};
}

// ============================================================================
// EYEBROW
// ============================================================================

export function Eyebrow({
	children,
	color = "var(--color-dashboard-muted)",
	style,
}: {
	children: React.ReactNode;
	color?: string;
	style?: React.CSSProperties;
}) {
	return (
		<div
			style={{
				fontFamily: "var(--font-mono)",
				fontSize: 10,
				fontWeight: 600,
				letterSpacing: "0.18em",
				textTransform: "uppercase",
				color,
				...style,
			}}
		>
			{children}
		</div>
	);
}

// ============================================================================
// ITALIC ACCENT
// ============================================================================

export function ItalicAccent({
	children,
	color = "var(--color-dashboard-accent-dark)",
	size = 18,
	style,
}: {
	children: React.ReactNode;
	color?: string;
	size?: number;
	style?: React.CSSProperties;
}) {
	return (
		<span
			style={{
				fontFamily: "var(--font-display)",
				fontStyle: "italic",
				fontWeight: 500,
				fontSize: size,
				color,
				letterSpacing: "-0.005em",
				...style,
			}}
		>
			{children}
		</span>
	);
}

/**
 * Format currency in INR with Cr/L shorthand (e.g. ₹6.4L or ₹6,40,000)
 */
export const formatINR = (n: number): string => {
	// The sign goes in front of the rupee symbol and the magnitude keeps its
	// L/Cr suffix. An escrow balance can be negative (`getProjectPayments`).
	if (n < 0) return `-${formatINR(-n)}`;
	if (n >= 10000000) {
		return `₹${(n / 10000000).toFixed(1)}Cr`;
	}
	if (n >= 100000) {
		return `₹${(n / 100000).toFixed(1)}L`;
	}
	return `₹${n.toLocaleString("en-IN")}`;
};

/**
 * Paise to rupees, for `formatINR` and for arithmetic beside rupee amounts.
 * BOQ amounts (`grandTotal`, `unitPrice`, `lineTotal`) and catalogue prices
 * arrive in paise. Escrow amounts, milestone `paymentAmount`, a designer's
 * `costEstimate`, a contractor's `quoteAmount` and the project budget arrive
 * in rupees. The selections tab formats its own shortlist quote prices, which
 * are also paise.
 */
export const paiseToRupees = (paise: number): number => paise / 100;

/** `formatINR` for an amount in paise: 7800 renders as ₹78. */
export const formatPaiseINR = (paise: number): string =>
	formatINR(paiseToRupees(paise));
